import { useSyncExternalStore } from 'react'
import { ApiError } from './api'
import type { SignLocale } from './locale'
import { contentHash } from './queue-hash'
import {
  allItems,
  backoffMs,
  deleteItem,
  itemByDedupe,
  itemById,
  MAX_ATTEMPTS,
  patchItem,
  putItem,
  type QueueError,
  type QueueItem,
  type QueuePayload,
  type RemarkPhotoRef,
  rankOf,
} from './queue-store'
import {
  type CreateRemarkBody,
  createSignRemark,
  type DrawnSubmission,
  type SignatureResource,
  type SignPhoto,
  type SignRemark,
  submitSignature,
  uploadSignPhoto,
} from './sign'

/**
 * The offline-light submission queue of M2-08.
 *
 * Every submission the sign PWA makes — a photo, a reservation, a signature — goes through here,
 * online or not. On a good connection that is one extra hop and the caller gets its answer as
 * before; in a loading bay with no signal the capture lands in IndexedDB instead, the driver gets a
 * screen that says so, and it leaves the device by itself the moment there is a network again.
 *
 * Three rules hold it together:
 *
 *  - **Order.** Photos before the reservation, the reservation before the signature, and never a
 *    later item of the same errand while an earlier one is still out. A signature spends the link,
 *    so one that overtakes its reservation costs that reservation for good (M1-23, M2-05).
 *  - **Exactly once.** The same capture enqueued twice is one item, an item is deleted only after
 *    the API answered, and only one runner is ever in flight — in this tab and, since #180, across
 *    every tab of this origin. A double tap, a reload halfway or a retry on an event costs one
 *    call, not two.
 *  - **Unchanged.** Each item carries the hash of what was captured and it is checked again on the
 *    way out, so nothing quietly differs between the ramp and the tunnel exit.
 */

/**
 * What a caller gets back: either the API's own answer, or the item that will carry it later. The
 * item comes along in both cases — a reservation refers to its photos by queue item whether they
 * are already up there or not, so the reference has to survive a call that succeeded straight away.
 */
export type Submitted<T> =
  | { readonly state: 'sent'; readonly item: QueueItem; readonly result: T }
  | { readonly state: 'queued'; readonly item: QueueItem }

export interface QueueSnapshot {
  readonly items: readonly QueueItem[]
  /** Items still on their way out: pending or in flight. */
  readonly waiting: number
  /** Items that stopped and need a person: a refusal, or a link somebody else already used. */
  readonly attention: number
  /** Everything that has not arrived yet, which is the number the header badge shows. */
  readonly outstanding: number
  /** False until the store has been read once, so a header does not flash an empty badge. */
  readonly ready: boolean
}

/**
 * Codes that mean the errand is over rather than postponed: the link was used, rotated or expired.
 * Retrying any of them would only ask the same question again, and the answer is a screen.
 */
const CONFLICT_CODES: ReadonlySet<string> = new Set([
  'already_signed',
  'conflict',
  'link_expired',
  'token_expired',
  'token_rotated',
])

/**
 * The name every tab of this origin queues behind.
 *
 * "One send in flight" used to live in `draining` alone, which is a module variable and therefore a
 * promise one tab makes to itself. Two tabs share one IndexedDB store, so both could pick up the
 * same pending item and send it; the second one was answered 409 `already_signed` — a single-use
 * token spends once — and the driver was shown a conflict that never happened (#180).
 */
const QUEUE_LOCK = 'freightapi-sign-queue'

const EMPTY: QueueSnapshot = { items: [], waiting: 0, attention: 0, outstanding: 0, ready: false }

let snapshot: QueueSnapshot = EMPTY
const listeners = new Set<() => void>()

/** Callers that are still on screen waiting for their own item; never restored after a reload. */
const waiters = new Map<string, ((outcome: Outcome) => void)[]>()

/**
 * Upload bars, by item. In memory for the same reason the waiters are: a photo that goes up hours
 * later, from a drain nobody is watching, has no bar to move and needs none.
 */
const progress = new Map<string, (fraction: number) => void>()

type Outcome =
  | { readonly state: 'sent'; readonly result: unknown }
  /** The row as the pass left it, when there is one; a row another tab carried leaves none. */
  | { readonly state: 'queued'; readonly item?: QueueItem }
  | { readonly state: 'refused'; readonly cause: ApiError }

let locale: SignLocale = 'nl'
let started = false
let draining: Promise<void> | undefined
let timer: ReturnType<typeof setTimeout> | undefined
let timerAt = Number.POSITIVE_INFINITY

/** The `Accept-Language` the runner sends with, kept in step with the language switch. */
export function setQueueLocale(next: SignLocale): void {
  locale = next
}

/**
 * Reads the queue and takes over from there: one drain now, because a phone that was closed in a
 * tunnel and reopened at the depot has work waiting, and one on every `online` event after that.
 */
export function startQueue(): void {
  if (started) return
  started = true

  if (typeof addEventListener === 'function') {
    addEventListener('online', () => void drainQueue())
  }

  void refresh().then(() => drainQueue())
}

export function subscribeToQueue(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function queueSnapshot(): QueueSnapshot {
  return snapshot
}

/** The queue as a screen sees it. Re-renders on every change, and on nothing else. */
export function useSubmissionQueue(): QueueSnapshot {
  return useSyncExternalStore(subscribeToQueue, queueSnapshot, () => EMPTY)
}

/** One errand: the photos, the reservation and the signature of a single visit share this id. */
export function newQueueGroup(): string {
  return crypto.randomUUID()
}

export interface PhotoSubmission {
  readonly token: string
  readonly group: string
  readonly file: File
  /** How much of this photo has left the device, while the screen that picked it is still open. */
  readonly onProgress?: (fraction: number) => void
}

export interface RemarkSubmission {
  readonly token: string
  readonly group: string
  readonly body: CreateRemarkBody
  /**
   * The photos this reservation refers to, in the order they were taken — by queue item, with the
   * document id filled in for the ones that already made it. A photo still on the queue has no
   * document id yet, and the runner writes it in the moment it gets one.
   */
  readonly photos: readonly RemarkPhotoRef[]
}

export interface SignatureSubmission {
  readonly token: string
  readonly group: string
  readonly body: DrawnSubmission
}

export async function queuePhoto(input: PhotoSubmission): Promise<Submitted<SignPhoto>> {
  return await submit<SignPhoto>(
    input.token,
    input.group,
    { kind: 'photo', blob: input.file, name: input.file.name },
    input.onProgress,
  )
}

export async function queueRemark(input: RemarkSubmission): Promise<Submitted<SignRemark>> {
  return await submit<SignRemark>(input.token, input.group, {
    kind: 'remark',
    body: input.body,
    photos: input.photos,
  })
}

export async function queueSignature(
  input: SignatureSubmission,
): Promise<Submitted<SignatureResource>> {
  return await submit<SignatureResource>(input.token, input.group, {
    kind: 'signature',
    body: input.body,
  })
}

/**
 * Puts a capture on the queue and, when there is a connection, waits for its turn.
 *
 * The caller is handed the API's answer when it arrives, and the item itself when it does not —
 * which is what turns a dropped connection into a confirmation screen instead of an error. A
 * refusal the queue cannot do anything about is thrown, so the screens of M2-03 still recognise an
 * expired or already-used link.
 */
async function submit<T>(
  token: string,
  group: string,
  payload: QueuePayload,
  onProgress?: (fraction: number) => void,
): Promise<Submitted<T>> {
  const item = await enqueue(token, group, payload)
  await refresh()

  if (onProgress !== undefined) progress.set(item.id, onProgress)
  if (!isOnline()) return { state: 'queued', item }

  const outcome = await new Promise<Outcome>((resolve) => {
    waiters.set(item.id, [...(waiters.get(item.id) ?? []), resolve])
    void drainQueue()
  })

  if (outcome.state === 'refused') throw outcome.cause
  return outcome.state === 'sent'
    ? { state: 'sent', item, result: outcome.result as T }
    : { state: 'queued', item: outcome.item ?? item }
}

/**
 * The item for this capture, whether or not it existed already.
 *
 * Deduplication is what makes a double submission harmless: the same content, for the same link, in
 * the same kind, is one row. An item that already stopped is revived rather than doubled — pressing
 * send again after a refusal is a retry, and a retry of a capture is not a second capture.
 */
async function enqueue(token: string, group: string, payload: QueuePayload): Promise<QueueItem> {
  const hash = await contentHash(payload)
  const dedupe = `${token}:${payload.kind}:${hash}`
  const existing = await itemByDedupe(dedupe)

  if (existing !== undefined) {
    if (existing.status === 'pending' || existing.status === 'sending') return existing
    return await putItem({
      ...existing,
      payload,
      attempts: 0,
      status: 'pending',
      nextAttemptAt: 0,
      error: undefined,
    })
  }

  return await putItem({
    id: crypto.randomUUID(),
    token,
    kind: payload.kind,
    payload,
    hash,
    group,
    createdAt: new Date().toISOString(),
    attempts: 0,
    status: 'pending',
    nextAttemptAt: 0,
    dedupe,
  })
}

/** Puts a stopped item back in line, together with anything of the same errand it was holding up. */
export async function retryItem(id: string): Promise<void> {
  const item = await itemById(id)
  if (item === undefined) return

  await revive(item)
  for (const other of await allItems()) {
    if (other.group === item.group && other.error?.code === 'queue_blocked') await revive(other)
  }

  await refresh()
  await drainQueue()
}

async function revive(item: QueueItem): Promise<void> {
  await putItem({ ...item, status: 'pending', attempts: 0, nextAttemptAt: 0, error: undefined })
}

/**
 * Throws a capture away for good. Only ever a person's decision: the queue never drops a submission
 * on its own, because the one thing worse than a signature that arrives late is one that silently
 * never arrives.
 */
export async function discardItem(id: string): Promise<void> {
  const item = await itemById(id)
  await deleteItem(id)
  // A screen may still be holding this item's promise. Nothing is going to arrive for it now, so
  // it is told that rather than left open for as long as the tab lives.
  if (item !== undefined) settle(id, { state: 'queued', item })
  await refresh()
}

/**
 * Works the queue until nothing is eligible. Only one pass runs at a time — within this tab because
 * of `draining`, and across the tabs of this origin because of `QUEUE_LOCK`. A call that arrives
 * during a pass queues another one behind it, so an item enqueued mid-drain never waits for an
 * event that may not come.
 */
export function drainQueue(): Promise<void> {
  if (draining !== undefined) return draining.then(() => drainQueue())

  draining = withQueueLock(runDrain).finally(() => {
    draining = undefined
  })
  return draining
}

/**
 * The Web Locks manager, where there is one. Absent in a context that is not secure and in any
 * runtime without the API, and there the fallback is what this file did before the lock existed:
 * one runner per tab, which is exactly the guarantee a single tab had all along.
 */
function lockManager(): LockManager | undefined {
  if (typeof navigator === 'undefined') return undefined
  return (navigator as Navigator & { locks?: LockManager }).locks
}

/**
 * Runs one pass as the only pass on this origin.
 *
 * The lock cannot become a way to hang the queue, and that is a property of the API rather than a
 * hope: the browser releases the lock the moment the callback's promise settles — fulfilled or
 * rejected, so a pass that throws gives it back on its way out — and releases every lock an agent
 * holds when that agent goes away, which covers a tab that is closed, navigated away or killed. A
 * document holding a lock is kept out of the back/forward cache rather than frozen while holding
 * it, so a phone that sleeps on this screen does not strand the other tab either.
 *
 * What is left is a request that never answers. That tab is alive and is working on the very item
 * the waiting tab wants, so waiting is the right thing to do with it — and a tab whose own send
 * hangs has been stuck on its own `draining` since M2-08 regardless.
 */
async function withQueueLock(run: () => Promise<void>): Promise<void> {
  const locks = lockManager()
  if (locks === undefined) {
    await run()
    return
  }

  await locks.request(QUEUE_LOCK, async () => {
    await run()
  })
}

async function runDrain(): Promise<void> {
  while (isOnline()) {
    const items = await allItems()
    publish(items)

    const next = items.find((item) => item.status === 'pending' && item.nextAttemptAt <= Date.now())
    if (next === undefined) {
      rest(items)
      schedule(items)
      return
    }

    const gate = gateOf(next, items)
    if (gate === 'wait') {
      rest(items)
      schedule(items)
      return
    }
    if (gate === 'broken') {
      await stop(next, 'failed', { code: 'queue_blocked' })
      settle(next.id, { state: 'refused', cause: blockedError() })
      continue
    }

    await sendOne(next)
  }

  rest(await allItems())
  publish(await allItems())
}

/**
 * Tells every caller still waiting on a pending item that it is going to be carried rather than
 * answered. The pass is over: what is left is either behind a backoff or behind an earlier item of
 * the same errand, and a screen that keeps spinning until either clears is a screen that lies about
 * what the queue is for.
 */
function rest(items: readonly QueueItem[]): void {
  for (const item of items) {
    if (item.status === 'pending' && waiters.has(item.id)) {
      settle(item.id, { state: 'queued', item })
    }
  }

  // A row that is gone from the store this pass just read, without this tab having been the one to
  // send it, was carried — or thrown away — by another tab. Waiting for the lock is precisely how
  // this tab ends up second, so a caller left spinning here would be the lock's own doing.
  for (const id of [...waiters.keys()]) {
    if (!items.some((item) => item.id === id)) settle(id, { state: 'queued' })
  }
}

/** A reservation whose photo was refused is not a reservation that can be recorded as captured. */
function blockedError(): ApiError {
  return new ApiError({
    status: 0,
    code: 'queue_blocked',
    messageKey: 'error.unexpected',
    detail: 'An earlier submission of the same errand did not arrive.',
  })
}

/**
 * Whether an item may go now. Everything earlier in the same errand has to be gone — delivered
 * items are deleted, so anything still there is either on its way (wait) or stopped (broken, and
 * this one cannot be sent as captured either).
 */
function gateOf(item: QueueItem, items: readonly QueueItem[]): 'ready' | 'wait' | 'broken' {
  const earlier = items.filter(
    (other) => other.group === item.group && rankOf(other.kind) < rankOf(item.kind),
  )
  if (earlier.some((other) => other.status === 'pending' || other.status === 'sending')) {
    return 'wait'
  }
  return earlier.length === 0 ? 'ready' : 'broken'
}

async function sendOne(item: QueueItem): Promise<void> {
  // The proof the ticket asks for, checked rather than claimed: what leaves is what was captured.
  if ((await contentHash(item.payload)) !== item.hash) {
    await stop(item, 'failed', { code: 'hash_mismatch' })
    return
  }

  const sending = await patchItem(item.id, (current) => ({
    ...current,
    status: 'sending',
    attempts: current.attempts + 1,
  }))
  if (sending === undefined) return
  publish(await allItems())

  try {
    await complete(sending, await dispatch(sending))
  } catch (cause) {
    await failed(sending, cause)
  }
}

async function dispatch(item: QueueItem): Promise<unknown> {
  switch (item.payload.kind) {
    case 'photo': {
      const { blob, name } = item.payload
      const onProgress = progress.get(item.id)
      return await uploadSignPhoto(item.token, new File([blob], name, { type: blob.type }), {
        locale,
        ...(onProgress === undefined ? {} : { onProgress }),
      })
    }

    case 'remark': {
      const documents = item.payload.photos.flatMap((photo) =>
        photo.documentId === undefined ? [] : [photo.documentId],
      )
      return await createSignRemark(
        item.token,
        {
          ...item.payload.body,
          ...(documents.length === 0 ? {} : { photo_document_ids: documents }),
        },
        { locale },
      )
    }

    case 'signature':
      return await submitSignature(item.token, item.payload.body, { locale })
  }
}

/**
 * An item that arrived. A photo hands its document id to the reservation waiting for it *before*
 * the photo itself is deleted, so a browser that is closed between the two never loses the link
 * between the picture and what it is evidence of.
 */
async function complete(item: QueueItem, result: unknown): Promise<void> {
  if (item.kind === 'photo') {
    const documentId = (result as SignPhoto).id
    for (const other of await allItems()) {
      if (other.payload.kind !== 'remark') continue
      if (!other.payload.photos.some((photo) => photo.itemId === item.id)) continue

      await putItem({
        ...other,
        payload: {
          ...other.payload,
          photos: other.payload.photos.map((photo) =>
            photo.itemId === item.id ? { ...photo, documentId } : photo,
          ),
        },
      })
    }
  }

  await deleteItem(item.id)
  await refresh()
  settle(item.id, { state: 'sent', result })
}

async function failed(item: QueueItem, cause: unknown): Promise<void> {
  const error = cause instanceof ApiError ? cause : undefined
  const detail = { code: error?.code ?? 'unexpected', detail: error?.message }

  if (error !== undefined && CONFLICT_CODES.has(error.code)) {
    await stop(item, 'conflict', detail)
    settle(item.id, { state: 'refused', cause: error })
    return
  }

  // Anything the server refused on its merits is refused for good: sending the same body again
  // would only be told the same thing, and a driver is owed the answer instead of a spinner.
  if (error === undefined || !error.isRetryable) {
    await stop(item, 'failed', detail)
    settle(item.id, {
      state: 'refused',
      cause:
        error ?? new ApiError({ status: 0, code: 'unexpected', messageKey: 'error.unexpected' }),
    })
    return
  }

  // A dropped connection, a 429 or a 5xx: the submission is fine, the moment was not.
  if (item.attempts >= MAX_ATTEMPTS) {
    const stopped = await stop(item, 'failed', detail)
    settle(item.id, { state: 'queued', item: stopped })
    return
  }

  const waiting = await patchItem(item.id, (current) => ({
    ...current,
    status: 'pending',
    nextAttemptAt: Date.now() + backoffMs(current.attempts),
    error: detail,
  }))
  await refresh()
  if (waiting !== undefined) settle(item.id, { state: 'queued', item: waiting })
}

async function stop(
  item: QueueItem,
  status: 'conflict' | 'failed',
  error: QueueError,
): Promise<QueueItem> {
  const stopped = { ...item, status, error }
  await putItem(stopped)
  await refresh()
  return stopped
}

function settle(id: string, outcome: Outcome): void {
  const waiting = waiters.get(id)
  waiters.delete(id)
  if (outcome.state !== 'queued') progress.delete(id)
  for (const resolve of waiting ?? []) resolve(outcome)
}

/** Wakes the runner when the first item comes out of its backoff, so nothing waits on an event. */
function schedule(items: readonly QueueItem[]): void {
  const due = items
    .filter((item) => item.status === 'pending')
    .map((item) => item.nextAttemptAt)
    .sort((left, right) => left - right)[0]

  if (due === undefined) {
    if (timer !== undefined) clearTimeout(timer)
    timer = undefined
    timerAt = Number.POSITIVE_INFINITY
    return
  }

  if (timer !== undefined && timerAt <= due) return
  if (timer !== undefined) clearTimeout(timer)
  timerAt = due
  timer = setTimeout(
    () => {
      timer = undefined
      timerAt = Number.POSITIVE_INFINITY
      void drainQueue()
    },
    Math.max(0, due - Date.now()),
  )
}

async function refresh(): Promise<void> {
  publish(await allItems())
}

function publish(items: readonly QueueItem[]): void {
  const waiting = items.filter(
    (item) => item.status === 'pending' || item.status === 'sending',
  ).length

  snapshot = {
    items,
    waiting,
    attention: items.length - waiting,
    outstanding: items.length,
    ready: true,
  }
  for (const listener of listeners) listener()
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

/** Drops every listener, timer and cached read. Only a test has a reason to call this. */
export function resetQueueRuntime(): void {
  if (timer !== undefined) clearTimeout(timer)
  timer = undefined
  timerAt = Number.POSITIVE_INFINITY
  started = false
  draining = undefined
  waiters.clear()
  progress.clear()
  listeners.clear()
  snapshot = EMPTY
  locale = 'nl'
}
